import { getApiDomain } from '@cartwhl/common/api/api-utils';

import { authStore } from 'stores';

/**
 * This store is used to store the webSocket connection and manage event subscriptions.
 * It uses an event emitter pattern for a simpler and more intuitive API.
 */

/* TYPES */

type WebSocketWithConnectionInfo = WebSocket & {
  connectionID?: string;
  endpoint?: string;
};

type WebSocketData =
  | {
      state: undefined;
      connectionID: string;
      endpoint: string;
    }
  | {
      state: '3D_CONVERT_COMPLETE';
      jobID: string;
    }
  | {
      state: '3D_CONVERT_STARTED';
      jobID: string;
    }
  | {
      state: 'BVH_READY';
      jobID: string;
      bvhURL: string;
      prompt: string;
    }
  | {
      state: 'EXPORT_READY';
      jobID?: string;
      projectID?: string;
      downloadURLs: Record<string, string>;
    }
  | {
      state: 'EXPORT_FAILED';
      jobID: string;
      errorMessages: string[];
    }
  | {
      state: 'EXPORT_WARNING';
      extendedProcessingTimeMs: number;
      jobID: string;
      warningMessage: string;
    }
  | {
      state: 'FAILED';
      jobID: string;
    }
  | {
      state: 'GENERATING_PK';
      jobID: string;
    }
  | {
      state: 'PROCESSING_VIDEO';
      jobID: string;
    }
  | {
      state: 'TIMELINE_UPDATED';
      jobID: string;
      projectID: string;
    }
  | {
      state: 'UPLOAD_COMPLETE';
      jobID: string;
      characterName: string;
    }
  | {
      state: 'UPLOAD_FAILED';
      jobID: string;
      characterName: string;
      errorMessages: string[];
    }
  | {
      state: 'UPLOAD_NEEDS_VALIDATION';
      jobID: string;
      characterName: string;
    }
  | {
      state: 'UPLOAD_WARNING';
      extendedProcessingTimeMs: number;
      jobID: string;
      warningMessage: string;
    }
  | {
      state: 'VIDEO_FLAGGED';
    };

type EventMap = {
  // Connection events
  open: Event;
  close: CloseEvent;
  error: Event;
  wake: MessageEvent<WebSocketData>;
  // Message events
  '3D_CONVERT_COMPLETE': Extract<
    WebSocketData,
    { state: '3D_CONVERT_COMPLETE' }
  >;
  '3D_CONVERT_STARTED': Extract<WebSocketData, { state: '3D_CONVERT_STARTED' }>;
  BVH_READY: Extract<WebSocketData, { state: 'BVH_READY' }>;
  EXPORT_READY: Extract<WebSocketData, { state: 'EXPORT_READY' }>;
  EXPORT_FAILED: Extract<WebSocketData, { state: 'EXPORT_FAILED' }>;
  EXPORT_WARNING: Extract<WebSocketData, { state: 'EXPORT_WARNING' }>;
  FAILED: Extract<WebSocketData, { state: 'FAILED' }>;
  GENERATING_PK: Extract<WebSocketData, { state: 'GENERATING_PK' }>;
  PROCESSING_VIDEO: Extract<WebSocketData, { state: 'PROCESSING_VIDEO' }>;
  TIMELINE_UPDATED: Extract<WebSocketData, { state: 'TIMELINE_UPDATED' }>;
  UPLOAD_COMPLETE: Extract<WebSocketData, { state: 'UPLOAD_COMPLETE' }>;
  UPLOAD_FAILED: Extract<WebSocketData, { state: 'UPLOAD_FAILED' }>;
  UPLOAD_NEEDS_VALIDATION: Extract<
    WebSocketData,
    { state: 'UPLOAD_NEEDS_VALIDATION' }
  >;
  UPLOAD_WARNING: Extract<WebSocketData, { state: 'UPLOAD_WARNING' }>;
  VIDEO_FLAGGED: Extract<WebSocketData, { state: 'VIDEO_FLAGGED' }>;
};

type EventHandler<T extends keyof EventMap> = (data: EventMap[T]) => void;

/* VARIABLES */

const PING_INTERVAL_MS = 30 * 1000; // 30 seconds

export let webSocket: WebSocketWithConnectionInfo;
let lastWebsocketMessageAt = -1;
let wsPingInterval: number;

const subscribers: Record<
  keyof EventMap,
  { id: string; handler: EventHandler<any> }[]
> = {
  // Connection events
  open: [],
  close: [],
  error: [],
  wake: [],
  // Message events
  '3D_CONVERT_COMPLETE': [],
  '3D_CONVERT_STARTED': [],
  BVH_READY: [],
  EXPORT_READY: [],
  EXPORT_FAILED: [],
  EXPORT_WARNING: [],
  FAILED: [],
  GENERATING_PK: [],
  PROCESSING_VIDEO: [],
  TIMELINE_UPDATED: [],
  UPLOAD_COMPLETE: [],
  UPLOAD_FAILED: [],
  UPLOAD_NEEDS_VALIDATION: [],
  UPLOAD_WARNING: [],
  VIDEO_FLAGGED: [],
};

/* EXPORTED FUNCTIONS */

export function on<T extends keyof EventMap>(
  event: T,
  handler: EventHandler<T>,
): () => void {
  const id = Math.random().toString(36).substring(7);
  subscribers[event].push({ id, handler });

  return () => {
    subscribers[event] = subscribers[event].filter((s) => s.id !== id);
  };
}

export async function connect(): Promise<void> {
  const wakeUp = () => webSocket?.send('ping');

  return new Promise((resolve, reject) => {
    if (webSocket && webSocket.readyState === WebSocket.OPEN) {
      return resolve();
    }

    const wsURL = getApiDomain('websocket');
    const ws: WebSocketWithConnectionInfo = new WebSocket(wsURL);

    ws.onopen = (event) => {
      ws.send(JSON.stringify({ action: 'ping', user: authStore.state.userID }));
      startWsPingInterval();
      webSocket = ws;
      window.addEventListener('focus', wakeUp);
      console.log('🔌 WebSocket connected');

      // Notify subscribers
      subscribers.open.forEach(({ handler }) => handler(event));
    };

    ws.onclose = (event) => {
      console.log('🔌 WebSocket disconnected. Reconnecting in 500ms.');
      window.removeEventListener('focus', wakeUp);

      // Notify subscribers
      subscribers.close.forEach(({ handler }) => handler(event));

      setTimeout(function () {
        connect();
      }, 500);
    };

    ws.onerror = (event) => {
      console.log('🔌 WebSocket error', event);

      // Notify subscribers
      subscribers.error.forEach(({ handler }) => handler(event));

      reject({ error: 'WebSocket could not be established' });
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data) as WebSocketData;

      // The state is undefined when the ping comes back with the current connectionID and endpoint
      if (data.state === undefined) {
        if (data.connectionID && data.endpoint) {
          ws.connectionID = data.connectionID;
          ws.endpoint = data.endpoint;
          resolve();
        }

        const now = new Date().getTime();
        const lastMessageWasLongTimeAgo =
          lastWebsocketMessageAt !== -1 &&
          now - lastWebsocketMessageAt > PING_INTERVAL_MS + 1000;

        lastWebsocketMessageAt = now;

        // If the last message was long time ago, we need to wake up the websocket
        // We assume that we are in a situation, when the last ping was longer than 31 seconds ago
        // This might happen when the user has minimized the browser window for a longer time
        if (lastMessageWasLongTimeAgo) {
          // Notify subscribers
          subscribers.wake.forEach(({ handler }) => handler(event));
        }
      } else {
        // All non-ping messages...
        // First log the message
        logMessageEvent(event);

        // Notify state-specific subscribers
        subscribers[data.state]?.forEach(({ handler }) => handler(data));
      }
    };
  });
}

/* HELPER FUNCTIONS */

// Ping interval is needed to keep the backend connection alive
// AWS kills idling connections after 10 minutes
function startWsPingInterval() {
  if (wsPingInterval) window.clearInterval(wsPingInterval);
  wsPingInterval = window.setInterval(() => {
    webSocket.send('ping');
  }, PING_INTERVAL_MS);
}

// There is a situation, where a user minimizes the browser window, which will pause the websocket connection, and after coming back,
// ...the job might be finished and/or the websocket might be dead. This is supposed to recover from this situation
// To understand how this works, have a look at the websockets onMessage handler, where `state === undefined`
function logMessageEvent(event: MessageEvent<WebSocketData>) {
  const data =
    typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

  console.log(
    `%c 🔔 ${data.state}`,
    'color: MediumSeaGreen; font-weight: bold;',
    data,
  );
}
